import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Briefcase, User, CheckCircle, Mail } from 'lucide-react';
import { BOHOL_TOWNS } from '../lib/constants';

/**
 * Role Selection Page - Progressive Profiling Step 1
 * Shown after social login (Google/Facebook) when user doesn't have a role set
 */
const RoleSelection = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedTown, setSelectedTown] = useState('');
  const [logoUrl, setLogoUrl] = useState('/logo-transparent.png');
  const [user, setUser] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showEmailField, setShowEmailField] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [hasReferrer, setHasReferrer] = useState(false);

  const fetchLogo = useCallback(async () => {
    try {
      const response = await api.get('/api/settings/version');
      if (response.data.logo_url) {
        setLogoUrl(response.data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  }, []);

  const checkUserAndRole = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Not logged in, redirect to sign-up
        // Set initialized to prevent safety timeout during redirect
        setInitialized(true);
        window.location.href = '/sign-up';
        return;
      }

      setUser(session.user);

      // Check if user already has a role set in profile
      // Use auth_user_id to find profile (not id, which is the profile's own UUID)
      // Use maybeSingle() to handle edge case of no profile gracefully
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, role, town, phone, years_of_experience, education_level, date_of_birth, business_name, industry, number_of_employees, referred_by, registration_source')
        .eq('auth_user_id', session.user.id)
        .maybeSingle();

      if (profileError) {
        console.error('[RoleSelection] Error fetching profile:', profileError);
      }

      if (profile?.role && profile.role !== 'pending') {
        // User already has a role, check if they need to complete profile
        const isJobSeeker = profile.role === 'job_seeker' || profile.role === 'contractor';
        const isEmployer = profile.role === 'employer' || profile.role === 'owner';
        // PESO/NSRP registrants are fully captured by the NSRP form — never route them
        // through profile completion (mirrors App.js / ProfileCompletion.js bypass).
        const isPesoRegistrant = typeof profile.registration_source === 'string' && profile.registration_source.startsWith('peso_');

        const needsProfileCompletion = !isPesoRegistrant && (
          !profile.phone || profile.phone.trim() === '' ||
          (isJobSeeker && (!profile.years_of_experience || !profile.education_level || !profile.date_of_birth)) ||
          (isEmployer && (!profile.business_name || !profile.industry || !profile.number_of_employees))
        );

        // CRITICAL: Set initialized before redirecting to prevent safety timeout from triggering
        setInitialized(true);

        if (needsProfileCompletion) {
          window.location.href = '/complete-profile';
        } else {
          window.location.href = '/dashboard';
        }
        return;
      }

      // Store profile ID and referrer status for update
      if (profile?.id) {
        setProfileId(profile.id);
        if (profile.referred_by) setHasReferrer(true);
      }

      // Pre-fill name from social login if available
      const userName = session.user.user_metadata?.full_name ||
                       session.user.user_metadata?.name ||
                       '';

      // Check if this is a Facebook user with no email
      const provider = session.user.app_metadata?.provider;
      if (provider === 'facebook' && !session.user.email) {
        console.warn('[RoleSelection] Facebook user has no email address');
        setShowEmailField(true);
      }

      console.log('[RoleSelection] User needs to select role:', session.user.email);
      setInitialized(true);
    } catch (error) {
      console.error('Error checking user role:', error);
      // Set initialized to prevent safety timeout during redirect
      setInitialized(true);
      window.location.href = '/sign-up';
    }
  }, []);

  // Run initialization only once on mount
  useEffect(() => {
    checkUserAndRole();
    fetchLogo();
  }, [checkUserAndRole, fetchLogo]);

  // CRITICAL: Safety timeout - separate effect to prevent re-calling init functions
  useEffect(() => {
    // Only set timeout if not yet initialized
    if (initialized) return;

    const safetyTimer = setTimeout(() => {
      console.error('[RoleSelection] Safety timeout - initialization took too long');
      window.location.href = '/sign-up';
    }, 10000);

    return () => clearTimeout(safetyTimer);
  }, [initialized]);

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error(t('messages.selectRole') || 'Please select your account type');
      return;
    }

    if (!selectedTown) {
      toast.error(t('messages.selectTown') || 'Please select your location');
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Session expired. Please login again.');
        window.location.href = '/sign-up';
        return;
      }

      // Get user metadata from social login
      const userMeta = session.user.user_metadata || {};
      const userName = userMeta.full_name || userMeta.name || userMeta.email?.split('@')[0] || 'User';
      // Handle avatar URL - Facebook returns picture as nested object {data: {url: '...'}}
      const rawPicture = userMeta.avatar_url || userMeta.picture || null;
      const avatarUrl = rawPicture && typeof rawPicture === 'object'
        ? rawPicture?.data?.url || null
        : rawPicture;

      // Get the profile ID to update
      let updateProfileId = profileId;

      // If we don't have a profileId yet, fetch it or create it
      if (!updateProfileId) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, referred_by')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (!profileData?.id) {
          // Profile doesn't exist yet - trigger may still be creating it
          // Wait briefly and retry fetch before attempting to create
          console.log('[RoleSelection] Profile not found, waiting for trigger to create it...');

          await new Promise(resolve => setTimeout(resolve, 1000));

          const { data: retryProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('auth_user_id', session.user.id)
            .maybeSingle();

          if (retryProfile?.id) {
            updateProfileId = retryProfile.id;
          } else {
            // Profile still doesn't exist - create it with id matching auth_user_id
            // This matches the trigger's behavior: id = auth_user_id = session.user.id
            console.log('[RoleSelection] Creating profile for:', session.user.id);

            // Include referral code if stored from OAuth flow
            let refCode = null;
            try { refCode = localStorage.getItem('referral_code'); } catch {}

            const { data: newProfile, error: createError } = await supabase
              .from('profiles')
              .upsert({
                id: session.user.id,  // CRITICAL: id must equal auth_user_id for consistency
                auth_user_id: session.user.id,
                email: session.user.email,
                name: userName,
                avatar_url: avatarUrl,
                role: selectedRole,
                town: selectedTown,
                ...(refCode ? { referred_by: refCode.toUpperCase() } : {}),
                updated_at: new Date().toISOString()
              }, {
                onConflict: 'id',  // Use 'id' as conflict key (has unique constraint)
                ignoreDuplicates: false
              })
              .select('id')
              .maybeSingle();

            if (createError) {
              console.error('Error creating profile:', createError);
              throw new Error('Failed to create profile. Please try again.');
            }

            updateProfileId = newProfile?.id || session.user.id;
          }
        } else {
          updateProfileId = profileData.id;
        }
      }

      // Update profile with role and basic info
      const updateData = {
        role: selectedRole,
        town: selectedTown,
        name: userName,
        avatar_url: avatarUrl,
        updated_at: new Date().toISOString()
      };

      // Include email if provided by a Facebook user with no email
      if (showEmailField && emailInput.trim()) {
        updateData.email = emailInput.trim();
      }

      // Apply referral code from localStorage (OAuth signups) — only if profile doesn't already have one
      try {
        const storedRefCode = localStorage.getItem('referral_code');
        if (storedRefCode && !hasReferrer) {
          updateData.referred_by = storedRefCode.toUpperCase();
        }
      } catch {}


      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', updateProfileId);

      if (updateError) {
        console.error('Error updating profile:', updateError);
        throw updateError;
      }

      toast.success(t('messages.profileUpdated') || 'Account created successfully!');

      // Clear referral code from localStorage after successful profile update
      try { localStorage.removeItem('referral_code'); } catch {}

      // Redirect to profile completion page
      // Use full page reload to ensure App.js properly initializes
      window.location.href = '/complete-profile';
    } catch (error) {
      console.error('Error setting role:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src={logoUrl} alt="Logo" className="h-16 w-16 object-contain" />
          </div>
          <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
            <CheckCircle size={20} />
            <span className="text-sm font-medium">{t('auth.accountCreated') || 'Account Created!'}</span>
          </div>
          <CardTitle className="text-2xl">{t('auth.oneMoreStep') || 'One More Step'}</CardTitle>
          <CardDescription>
            {t('auth.selectRoleDescription') || 'Tell us how you\'ll use Bohol Trabaho'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Email input for Facebook users with no email */}
          {showEmailField && (
            <div className="space-y-2 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 mb-1">
                <Mail size={16} />
                <span className="text-sm font-medium">{t('auth.noEmailFromFacebook')}</span>
              </div>
              <Label htmlFor="email" className="text-sm text-muted-foreground">
                {t('auth.provideEmailOptional')}
              </Label>
              <Input
                id="email"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder={t('auth.emailAddress')}
              />
            </div>
          )}

          {/* Role Selection */}
          <div className="space-y-3">
            <Label className="text-base font-medium">{t('auth.iAmA') || 'I am a...'}</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectedRole('job_seeker')}
                className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                  selectedRole === 'job_seeker'
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  selectedRole === 'job_seeker' ? 'bg-primary text-white' : 'bg-gray-100'
                }`}>
                  <User size={24} />
                </div>
                <span className="font-medium text-sm">{t('auth.jobSeeker') || 'Job Seeker'}</span>
                <span className="text-xs text-muted-foreground text-center">
                  {t('auth.lookingForWork') || 'Looking for jobs & gigs'}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedRole('employer')}
                className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                  selectedRole === 'employer'
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  selectedRole === 'employer' ? 'bg-primary text-white' : 'bg-gray-100'
                }`}>
                  <Briefcase size={24} />
                </div>
                <span className="font-medium text-sm">{t('auth.employer') || 'Employer'}</span>
                <span className="text-xs text-muted-foreground text-center">
                  {t('auth.hiringTalent') || 'Hiring local talent'}
                </span>
              </button>
            </div>
          </div>

          {/* Location Selection */}
          <div className="space-y-2">
            <Label htmlFor="town">{t('profile.location') || 'Your Location'}</Label>
            <Select value={selectedTown} onValueChange={setSelectedTown}>
              <SelectTrigger>
                <SelectValue placeholder={t('post.selectLocation') || 'Select your town'} />
              </SelectTrigger>
              <SelectContent>
                {BOHOL_TOWNS.map((town) => (
                  <SelectItem key={town} value={town}>{town}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Continue Button */}
          <Button 
            onClick={handleSubmit} 
            className="w-full" 
            size="lg"
            disabled={loading || !selectedRole || !selectedTown}
          >
            {loading ? t('common.loading') : (t('auth.continue') || 'Continue')}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            {t('auth.completeProfileLater') || 'You can complete your full profile anytime from your dashboard'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleSelection;
